"use client"

import { useState } from "react"
import { isBlockPin, isQuiltPin, type PinRecord } from "@/lib/pins"
import { projectFromArtifact, type Artifact } from "@/lib/artifact"
import { renderBlock, DEFAULT_BLOCK_COLORS } from "@/lib/core/block_svg"
import { fullQuiltSvg } from "@/lib/core/layout"
import { schemaLabel } from "@/lib/core/blockschema"
import PinToggle from "./PinToggle"
import ZoomableViewer from "./ZoomableViewer"

interface ScrapbookProps {
  pins: PinRecord[]
  pinnedIds: Set<string>
  // Toggle pin state — same callback as the inline overlays in chat. In the
  // scrapbook this is always an unpin (every shown item is already pinned),
  // but using the same toggle keeps the fullscreen viewer's pin button
  // consistent with the rest of the app.
  onTogglePin: (artifact: Artifact, id: string) => void
  // Open this pin as a fresh project. Triggered by the "Start new project"
  // CTA inside the fullscreen viewer rather than by the gallery click —
  // gallery clicks open the viewer first.
  onOpenPin: (pin: PinRecord) => void
  // When set, render a close button in the header. The page passes this
  // only for the narrow-viewport drawer; the persistent wide-viewport
  // sidebar leaves it undefined so no close button appears.
  onClose?: () => void
}

// Scrapbook — a gallery of pinned designs. Clicking a card opens the
// fullscreen viewer for that design; the viewer carries a "Start new
// project" CTA that materializes a fresh chat session from the artifact.
//
// Layout-agnostic: this component fills its container and scrolls
// vertically. The page wraps it in either a persistent right-hand sidebar
// (wide viewports) or a full-width drawer (narrow viewports) — both
// branches mount the same component.
export default function Scrapbook({ pins, pinnedIds, onTogglePin, onOpenPin, onClose }: ScrapbookProps) {
  const [zoomItem, setZoomItem] = useState<PinRecord | null>(null)

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center gap-2 px-3 pt-3 pb-2 border-b border-border">
        <h2 className="font-display text-2xl text-foreground">Scrapbook</h2>
        <span className="text-sm text-muted-foreground">
          {pins.length === 0 ? "" : `${pins.length} pin${pins.length === 1 ? "" : "s"}`}
        </span>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close scrapbook"
            className="ml-auto p-1.5 rounded-md text-muted-foreground hover:bg-muted active:bg-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer"
          >
            <CloseIcon />
          </button>
        )}
      </div>

      {pins.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
          {pins.map((pin) => (
            <PinCard
              key={pin.id}
              pin={pin}
              pinned={pinnedIds.has(pin.id)}
              onView={() => setZoomItem(pin)}
              onTogglePin={() => onTogglePin(pin.artifact, pin.id)}
            />
          ))}
        </div>
      )}

      {zoomItem && (
        <ZoomableViewer
          svg={renderArtifactSvg(zoomItem.artifact, "viewer")}
          ariaLabel={pinCaption(zoomItem)}
          onClose={() => setZoomItem(null)}
          pinOverlay={
            <PinToggle
              pinned={pinnedIds.has(zoomItem.id)}
              onToggle={() => onTogglePin(zoomItem.artifact, zoomItem.id)}
              alwaysVisible
              layout="free"
            />
          }
          onNewProject={() => {
            onOpenPin(zoomItem)
            setZoomItem(null)
          }}
          newProjectLabel={`New project from this ${zoomItem.artifact.kind}`}
        />
      )}
    </div>
  )
}

function EmptyState() {
  return (
    <div className="flex-1 flex items-center justify-center px-6 py-10">
      <div className="text-center text-base text-muted-foreground space-y-2 max-w-xs">
        <p className="font-medium text-foreground">Empty scrapbook</p>
        <p>Hover any block or quilt and click the pin to save it here.</p>
      </div>
    </div>
  )
}

function PinCard({ pin, pinned, onView, onTogglePin }: {
  pin: PinRecord
  pinned: boolean
  onView: () => void
  onTogglePin: () => void
}) {
  const preview = renderArtifactSvg(pin.artifact, "thumbnail")
  const caption = pinCaption(pin)
  return (
    <div className="relative group rounded-lg border border-border bg-background overflow-hidden">
      <button
        type="button"
        onClick={onView}
        aria-label={`View ${caption} fullscreen`}
        className="block w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-shadow hover:shadow-md cursor-zoom-in"
      >
        <div
          className="block bg-background [&>svg]:block [&>svg]:w-full [&>svg]:h-auto"
          dangerouslySetInnerHTML={{ __html: preview }}
        />
        <div className="px-2 py-1.5 text-sm border-t border-border bg-black/[.02]">
          <span className="text-foreground truncate block">{caption}</span>
          <span className="text-xs text-muted-foreground capitalize">{pin.artifact.kind}</span>
        </div>
      </button>
      <PinToggle pinned={pinned} onToggle={onTogglePin} alwaysVisible />
    </div>
  )
}

function renderArtifactSvg(artifact: Artifact, mode: "thumbnail" | "viewer"): string {
  const blockScale = mode === "viewer" ? 60 : 36
  const quiltBlockPx = mode === "viewer" ? 48 : 24
  if (artifact.kind === "block") {
    // Pad missing slots with schema defaults so partial colorways still
    // render, matching the inline preview's behavior.
    const colors = { ...DEFAULT_BLOCK_COLORS, ...artifact.colorway }
    return renderBlock(artifact.block, blockScale, colors)
  }
  // Quilt artifact — reconstitute a Project (the renderer expects one) and
  // hand it to fullQuiltSvg.
  const project = projectFromArtifact(artifact)
  return fullQuiltSvg(project, quiltBlockPx)
}

function pinCaption(pin: PinRecord): string {
  if (isBlockPin(pin)) return schemaLabel(pin.artifact.block.style)
  if (isQuiltPin(pin)) return pin.artifact.projectName || "Untitled quilt"
  return "Pin"
}

function CloseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  )
}
